import { ClipboardCheck, FileText, Users2, Route } from 'lucide-react';

const helpItems = [
  'Understanding whether a student may qualify for accommodations',
  'Reviewing existing evaluations and identifying gaps',
  'Guidance on obtaining appropriate educational or neuropsychological testing',
  'Support preparing strong, well-documented accommodation requests',
  'Coaching families on working with schools and SSD coordinators',
  'Timeline planning to ensure applications are submitted on time'
];

const targetAudience = [
  'Students with ADHD, dyslexia, or other learning differences',
  'Students who consistently need more time on tests or assignments',
  'Families who feel unsure how to start or what documentation is required',
  'Students without a clear roadmap for school or testing accommodations'
];

const steps = [
  {
    icon: ClipboardCheck,
    number: '01',
    title: 'Initial Consultation',
    description: 'We assess your student\'s profile and likelihood of qualifying'
  },
  {
    icon: FileText,
    number: '02',
    title: 'Documentation Strategy',
    description: 'We identify what\'s needed and where gaps exist'
  },
  {
    icon: Users2,
    number: '03',
    title: 'Application Support',
    description: 'We guide you through building a strong, complete submission'
  },
  {
    icon: Route,
    number: '04',
    title: 'Ongoing Guidance',
    description: 'We help troubleshoot next steps if needed'
  }
];

const AccommodationsSection = () => {
  return (
    <section data-testid="accommodations-section" className="py-16 md:py-24 lg:py-32 bg-background border-t border-border/20">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-primary mb-6">
            Accommodations Navigation Support
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-accent font-medium max-w-3xl mx-auto">
            Helping families navigate testing accommodations with clarity, integrity, and confidence.
          </p>
        </div>

        {/* Overview */}
        <div className="max-w-4xl mx-auto mb-16">
          <p className="text-lg leading-relaxed text-muted-foreground text-center">
            Navigating standardized testing accommodations can feel overwhelming—especially for families unfamiliar with the process. We provide thoughtful, step-by-step guidance to help students with legitimate needs access appropriate accommodations, including extended time. Our approach is grounded in clarity, strong documentation, and alignment with school and testing requirements.
          </p>
        </div>

        {/* Two Column Layout: What We Help With & Who This Is For */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* What We Help With */}
          <div className="bg-muted p-8 md:p-10">
            <h3 className="text-2xl md:text-3xl font-heading font-medium text-primary mb-6">
              What We Help With
            </h3>
            <ul className="space-y-4">
              {helpItems.map((item) => (
                <li key={item} className="flex items-start">
                  <div className="bg-accent/10 rounded-full p-1.5 mr-3 mt-1 flex-shrink-0">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                  </div>
                  <span className="text-base leading-relaxed text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Who This Is For */}
          <div className="bg-white border border-border/40 p-8 md:p-10">
            <h3 className="text-2xl md:text-3xl font-heading font-medium text-primary mb-6">
              Who This Is For
            </h3>
            <ul className="space-y-4">
              {targetAudience.map((item) => (
                <li key={item} className="flex items-start">
                  <div className="bg-primary/10 rounded-full p-1.5 mr-3 mt-1 flex-shrink-0">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  </div>
                  <span className="text-base leading-relaxed text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-12">
          <h3 className="text-3xl md:text-4xl font-heading font-semibold text-primary text-center mb-12">
            How It Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.number}
                  data-testid={`step-${step.number}`}
                  className="text-center group"
                >
                  <div className="relative mb-6">
                    <div className="bg-accent/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto group-hover:bg-accent/20 transition-colors">
                      <Icon className="text-accent" size={32} />
                    </div>
                    <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold font-heading">
                      {step.number}
                    </div>
                  </div>
                  <h4 className="text-xl font-heading font-medium text-primary mb-3">
                    {step.title}
                  </h4>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a
            href="#contact"
            data-testid="accommodations-cta"
            className="inline-block bg-accent text-accent-foreground px-10 py-4 rounded-full text-lg font-medium hover:bg-accent/90 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg"
          >
            Schedule a Consultation
          </a>
          <p className="text-sm text-muted-foreground mt-4 max-w-2xl mx-auto">
            Not legal advice. Educational consulting and advocacy support to help families navigate the accommodations process.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AccommodationsSection;
